import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';
import { Box, Text, Title } from '@nerdwallet/currency';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
import _ from 'lodash';

import {
  repeatable,
  boolCheckboxSpec,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import getTextColor from '../../lib/text-color-config';
import parseMarkdownLinks from '../../lib/parse-md-links';

import BackgroundColor from '../background-color/background-color';

// TODO: fix import cycle
// eslint-disable-next-line import/no-cycle
import GridForm from '../grid-form/grid-form';
import Row from '../row/row';
import VerticalSpacer from '../vertical-spacer/vertical-spacer';
import BoxTableBox from './box-table-box';
import { getTextColorStyle } from '../../lib/helpers';

export const backgroundStyle = (bgColor, { title, margin, size }) => {
  let bgSize = '7%';
  if (title) {
    bgSize = '8.5%';
  }
  if (size === 'sm' && margin) {
    bgSize = '10.5%';
  }
  if (bgColor && margin) {
    return {
      background: `linear-gradient(to bottom, transparent ${bgSize}, ${bgColor} ${bgSize})`,
    };
  }
  if (bgColor && !margin) {
    return {
      background: bgColor,
    };
  }
  return {};
};

const setStickyHeaderBehavior = (flexCardElement, stickyHeaderElement) => {
  if (!flexCardElement || !stickyHeaderElement) return;
  const stickyHeaderComponent = stickyHeaderElement;
  const elementHight = 120;

  if (flexCardElement.getBoundingClientRect().top < -elementHight) {
    stickyHeaderComponent.style.position = 'fixed';
    stickyHeaderComponent.style.top = 0;
  } else {
    stickyHeaderComponent.style.top = '-50%';
  }
};

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const BoxTable = ({
  boxes,
  columns,
  negativeMargin,
  use2021Layout,
  useSkinnyLayout,
  dropdownButtonText,
  size,
  bgOptions,
  awardsYear,
  title,
  titleInChronicle,
  titleSize,
  subtitle,
  subtitleColorOptions,
  enableHtmlInSubtitle,
  negativeMarginMobile,
  shouldTrack,
  titleColorOptions,
  titleInBold,
  disableStickyHeaderIntegration,
}) => {
  const negativeTopMargin = {
    marginTop: -45,
  };

  const negativeMobileTopMargin = {
    marginTop: -80,
  };

  const noTopMargin = {
    marginTop: 0,
  };

  const is2022Layout = awardsYear === '2022';
  // This should be applied only for 2022 layout because we have 2022 configuration for that
  // Add a year condition if you have additional requirements for your layout
  let topMarginStyle =
    is2022Layout && negativeMargin ? negativeTopMargin : noTopMargin;

  if (is2022Layout && negativeMarginMobile && size !== 'lg') {
    topMarginStyle = negativeMobileTopMargin;
  }

  const formatIntoViewConfig = (box) => ({
    value: box.linkUrl,
    label: box.title,
  });

  const gotoSection = (box) => {
    window.location.href = box.linkUrl;
  };

  const is2022SmallLayout = is2022Layout && size !== 'lg';
  const is2023Layout = awardsYear === '2023';

  const colorStyle =
    is2023Layout || use2021Layout ? getTextColorStyle(titleColorOptions) : null;
  const boxTableRef = useRef();

  useEffect(() => {
    const stickyHeader = document.getElementById('sticky-header');
    if (stickyHeader) {
      stickyHeader.style.position = 'fixed';
      stickyHeader.style.display = 'flex';
      stickyHeader.style.top = '-50%';
    }
    const handleScroll = () => {
      setStickyHeaderBehavior(boxTableRef.current, stickyHeader);
    };

    if (!disableStickyHeaderIntegration && stickyHeader) {
      window.addEventListener('scroll', handleScroll);
    } else if (stickyHeader) {
      stickyHeader.style.position = 'sticky';
      stickyHeader.style.top = '0';
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [disableStickyHeaderIntegration]);

  const boxSections = boxes.map((box) => (
    <BoxTableBox
      {...box}
      key={`${box.title}-${box.subtitle}`}
      use2021Layout={use2021Layout}
      layout={`layout-${awardsYear}`}
      shouldTrack={shouldTrack}
    />
  ));

  const titleLayout = (
    <React.Fragment>
      {title && (
        <Box
          style={colorStyle}
          className={classNames([
            'box-table__boxes-title',
            `layout-${awardsYear}`,
            { 'title-in-bold': titleInBold },
            { 'title-skinny-layout': useSkinnyLayout },
          ])}
        >
          {is2023Layout || useSkinnyLayout ? (
            <Title
              className={classNames([{ 'title-chronicle': titleInChronicle }])}
              style={getTextColorStyle(titleColorOptions)}
              size={titleSize || 'small'}
            >
              {title}
            </Title>
          ) : (
            <Text bold>{title}</Text>
          )}
        </Box>
      )}
    </React.Fragment>
  );

  const subtitleLayout = (
    <React.Fragment>
      {subtitle && useSkinnyLayout && (
        <Box
          style={getTextColorStyle(subtitleColorOptions)}
          className="subtitle"
        >
          <Text>{htmlOutputParser(subtitle, enableHtmlInSubtitle)}</Text>
        </Box>
      )}
    </React.Fragment>
  );

  const smLayoutBoxes = (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          ref={boxTableRef}
          className={classNames([classes('padding-horizontal-3')])}
          style={{
            ...topMarginStyle,
            ...backgroundStyle(backgroundColor, {
              title,
              margin: is2023Layout ? null : negativeMarginMobile,
              size,
            }),
          }}
        >
          {titleLayout}
          {subtitleLayout}
          <Row
            allowColumnsInMobile={is2023Layout || useSkinnyLayout}
            className={classNames([
              'box-table__mobile-boxes',
              `layout-${awardsYear}`,
              {
                'skinny-layout': useSkinnyLayout,
              },
            ])}
            columns="2"
            centeredInParent
          >
            {boxSections}
            {!useSkinnyLayout && (
              <VerticalSpacer mobile="p-12" tablet="p-12" desktop="p-18" />
            )}
          </Row>
          {!useSkinnyLayout && <VerticalSpacer tablet="p-40" desktop="p-40" />}
        </div>
      )}
    </BackgroundColor>
  );

  const smLayoutDropdown = (
    <Box
      className={classNames([
        'box-table__mobile',
        classes('padding-horizontal-3'),
        classes('margin-bottom-3'),
        classes('max-width-prose-narrow'),
        classes('margin-horizontal-auto'),
      ])}
    >
      <GridForm
        initialState={{
          form: {
            linkUrl: _.get(
              boxes.filter((box) => box.type !== 'inactive'),
              '0.linkUrl'
            ),
          },
        }}
        formConfig={[
          {
            items: boxes
              .filter((box) => box.type !== 'inactive')
              .map(formatIntoViewConfig),
            viewType: 'select',
            keys: [{ key: 'form.linkUrl' }],
            value: '{{0}}',
          },
        ]}
        formName="queryFilters"
        orientation="vertical"
        columns="1"
        buttonText={dropdownButtonText}
        onClick={gotoSection}
      />
    </Box>
  );

  const lgLayout = (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <div
          ref={boxTableRef}
          className={classNames(['box-table__boxes', `layout-${awardsYear}`])}
          style={{
            ...topMarginStyle,
            ...backgroundStyle(backgroundColor, {
              title,
              margin: negativeMargin,
              size,
            }),
          }}
        >
          {titleLayout}
          {subtitleLayout}
          <Row
            centeredInParent
            standardMaxWidths={is2022SmallLayout}
            columns={columns}
            className={classNames([
              'box-table__boxes-wrapper',
              {
                'wide-layout': !is2022SmallLayout,
              },
              {
                'skinny-layout': useSkinnyLayout,
              },
            ])}
          >
            {boxSections}
            {!is2022Layout && (
              <VerticalSpacer mobile="p-12" tablet="p-12" desktop="p-18" />
            )}
          </Row>
          {!use2021Layout ? (
            <VerticalSpacer tablet="p-40" desktop="p-40" />
          ) : null}
        </div>
      )}
    </BackgroundColor>
  );

  const setLayout = () => {
    switch (true) {
      case size === 'sm' && use2021Layout:
      case size !== 'lg' && awardsYear === '2022':
      case size === 'sm' && useSkinnyLayout:
        return smLayoutBoxes;
      case size === 'lg':
      case size === 'md' && use2021Layout:
      case (size === 'lg' || size === 'md') && useSkinnyLayout:
        return lgLayout;
      case (size === 'sm' || size === 'md') &&
        !use2021Layout &&
        !useSkinnyLayout:
        return smLayoutDropdown;
      default:
        return null;
    }
  };

  return (
    <div
      id={
        disableStickyHeaderIntegration
          ? 'box-table'
          : 'box-table-sticky-header-integration'
      }
    >
      {setLayout()}
    </div>
  );
};

BoxTable.propTypes = {
  columns: Row.propTypes.columns,
  boxes: PropTypes.arrayOf(PropTypes.shape(BoxTableBox.propTypes)),
  negativeMargin: PropTypes.bool,
  use2021Layout: PropTypes.bool,
  useSkinnyLayout: PropTypes.bool,
  awardsYear: PropTypes.string,
  disableStickyHeaderIntegration: PropTypes.bool,
  ...BackgroundColor.propTypes,
  dropdownButtonText: PropTypes.string,
  size: PropTypes.string,
};

BoxTable.getEditingConfig = (componentDocument) => {
  let defaultLayoutEditing = [];
  if (
    _.get(componentDocument, 'props.use2021Layout', false) &&
    componentDocument?.props?.awardsYear !== '2022'
  ) {
    defaultLayoutEditing = [
      stringInputSpec('dropdownButtonText', 'Dropdown button text'),
    ];
  }
  if (componentDocument?.props?.awardsYear !== '2022') {
    defaultLayoutEditing = [
      ...defaultLayoutEditing,
      boolCheckboxSpec('negativeMargin', 'Add negative top margin on desktop?'),
    ];
  }
  if (componentDocument?.props?.awardsYear === '2022') {
    defaultLayoutEditing = [
      stringInputSpec('title', 'Title'),
      boolCheckboxSpec(
        'negativeMarginMobile',
        'Add negative top margin on mobile?'
      ),
    ];
  }

  const titleColorInputConfig = [
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
  ];
  if (componentDocument?.props?.awardsYear === '2023') {
    defaultLayoutEditing = [
      stringInputSpec('title', 'Title'),
      boolCheckboxSpec('titleInBold', 'Title in Bold?'),
      ...titleColorInputConfig,
    ];
  }

  const awardsYearConfig = componentDocument?.props?.use2021Layout
    ? [
        stringSelectSpec('awardsYear', 'Awards Year', [
          { label: '2021', value: '2021' },
          { label: '2022', value: '2022' },
          { label: '2023', value: '2023' },
          { label: '2020', value: '2020' },
        ]),
      ]
    : [];

  const skinnyLayoutInputsConfig = componentDocument?.props?.useSkinnyLayout
    ? [
        stringInputSpec('title', 'Title'),
        boolCheckboxSpec('titleInChronicle', 'Title In Chronicle?'),
        stringSelectSpec(
          `titleSize`,
          'Title Size',
          [
            { label: 'Small', value: 'small' },
            { label: 'Default', value: 'default' },
          ],
          {
            help: 'Defaults to small',
          }
        ),
        ...titleColorInputConfig,
        stringInputSpec('subtitle', 'Subtitle'),
        ...getTextColor.getEditingConfig(
          componentDocument,
          'subtitleColorOptions',
          'Subtitle Color'
        ),
        boolCheckboxSpec('enableHtmlInSubtitle', 'Allow HTML in Subtitle?', {
          help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
        }),
      ]
    : [];

  const skinnyLayoutInput = !componentDocument?.props?.use2021Layout
    ? [
        boolCheckboxSpec('useSkinnyLayout', 'Use Skinny Layout?'),
        ...skinnyLayoutInputsConfig,
      ]
    : [];
  return [
    ...Row.getEditingConfig(),
    ...BackgroundColor.getEditingConfig(componentDocument),
    boolCheckboxSpec('use2021Layout', 'Use BOA Layout?'),
    ...skinnyLayoutInput,
    ...awardsYearConfig,
    ...defaultLayoutEditing,
    boolCheckboxSpec(
      'shouldTrack',
      'Add BOA Awards analytics instrumentation?'
    ),
    boolCheckboxSpec(
      'disableStickyHeaderIntegration',
      'Disable StickyHeader Integration when Scrolling?'
    ),
    repeatable('boxes', 'Elements', 'Element', (i) =>
      BoxTableBox.getEditingConfig(
        _.get(componentDocument, `props.boxes[${i}]`)
      )
    ),
  ];
};

BoxTable.defaultProps = {
  boxes: [],
  columns: '1',
  negativeMargin: false,
  use2021Layout: false,
  dropdownButtonText: 'Click me',
  awardsYear: '2021',
  disableStickyHeaderIntegration: false,
  size: '',
};

BoxTable.displayName = 'BoxTable';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(BoxTable);
